import {createEmpty, extend, Extent, isEmpty} from "ol/extent";

/**
 * The type of unit to format.
 */
export type UnitType = "area" | "length";
/**
 * The format type.
 */
export type UnitFormat = "label" | undefined;
/**
 * Converts degrees to radians.
 *
 * @param {number} degrees - The angle in degrees.
 * @returns {number} The angle in radians.
 *
 * @example
 * // Convert 180 degrees to radians
 * const radians = degreesToRadians(180); // Math.PI
 */
export function degreesToRadians(degrees: number) {
    return degrees * Math.PI * 2 / 360;
}
/**
 * Converts radians to degrees.
 *
 * @param {number} radians - The angle in radians.
 * @returns {number} The angle in degrees.
 *
 * @example
 * // Convert Math.PI radians to degrees
 * const degrees = radiansToDegrees(Math.PI); // 180
 */
export function radiansToDegrees(radians: number) {
    return radians * 360 / (Math.PI * 2);
}
/**
 * Formats a given numeric value into a specific unit type and format.
 *
 * @param value - The numeric value to format.
 * @param type - The type of unit, either "area" or "length".
 * @param format - The format type, currently supports "label".
 * @returns The formatted value as a string with units if the format is "label", or the numeric value otherwise.
 *
 * @example
 * // Format an area value in square meters to hectares or square kilometers with a label.
 * const area = formatUnits(15000000, 'area', 'label'); // "15 km²"
 *
 * @example
 * // Format a length value in meters to kilometers or meters with a label.
 * const length = formatUnits(500, 'length', 'label'); // "500 m"
 */
export function formatUnits(value: number, type: UnitType, format: UnitFormat) {
    if (format !== "label") {
        return Math.round(value * 100) / 100;
    }


    const userLocales = ['fi-FI', 'cs-CZ', 'sv-SE'];
    const formatter = new Intl.NumberFormat(userLocales, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: true
    });

    const f = (val: number) => formatter.format(Math.round(val * 100) / 100);

    switch (type) {
        case "length":
            if (value >= 1000) return `${f(value / 1000)} km`;
            if (value >= 1) return `${f(value)} m`;
            if (value >= 0.01) return `${f(value * 100)} cm`;
            return `${f(value * 1000)} mm`;

        case "area":
            if (value >= 100) return `${f(value / 10000)} ha`;
            if (value >= 0.1) return `${f(value)} m²`;
            if (value >= 0.0001) return `${f(value * 10000)} cm²`;
            return `${f(value * 1000000)} mm²`;

        default:
            return f(value);
    }
}

export function extendExtent(extent1:Extent, extent2:Extent) {
    if (isEmpty(extent1)) {
        extent1 = createEmpty();
    }

    if (isEmpty(extent2)) {
        extent2 = createEmpty();
    }

    return extend(extent1, extent2);
}